import React, { createContext, useState, useContext, useCallback } from "react";
import { supabase } from "../supabaseClient";

// Define interfaces for our data
export interface FormData {
  checkIn: Date | undefined;
  checkOut: Date | undefined;
  rooms: string;
  adults: string;
  children: string;
  promoCode: string;
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
  specialRequests?: string;
  // Address fields
  guestAddress?: string;
  guestCity?: string;
  guestZipCode?: string;
  guestCountry?: string;
}

export interface RoomSelection {
  id: string;
  type: string;
  count: number;
  price: number;
}

export interface ExtraService {
  id: string;
  name: string;
  selected: boolean;
  price: number;
  quantity?: number; // Optional quantity for new format
}

export interface CustomerInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  zipCode: string;
  country: string;
  specialRequests: string;
  paymentMethod: string;
}

export interface SelectedHotel {
  id: string;
  name: string;
  slug: string;
  // Add other fields as needed (e.g., email, phone, etc.)
}

interface BookingContextType {
  formData: FormData;
  updateFormData: (field: keyof FormData, value: any) => void;
  selectedRooms: RoomSelection[];
  updateSelectedRooms: (rooms: RoomSelection[]) => void;
  selectedExtras: ExtraService[];
  updateSelectedExtras: (extras: ExtraService[]) => void;
  customerInfo: CustomerInfo;
  updateCustomerInfo: (field: keyof CustomerInfo, value: string) => void;
  totalPrice: number;
  calculateTotalPrice: () => number;
  confirmationNumber: string | null;
  setConfirmationNumber: (number: string) => void;
  resetBooking: () => void;
  hotelSlug: string;
  setHotelSlug: (slug: string) => void;
  isEmbedded: boolean;
  setIsEmbedded: (embedded: boolean) => void;
  selectedHotel: SelectedHotel | null;
  setSelectedHotel: (hotel: SelectedHotel | null) => void;
  fetchAndSetHotel: (slug: string) => Promise<void>;
  currentStep: number;
  setCurrentStep: (step: number) => void;
}

const defaultFormData: FormData = {
  checkIn: undefined,
  checkOut: undefined,
  rooms: "1",
  adults: "1",
  children: "0",
  promoCode: "",
  guestName: "",
  guestEmail: "",
  guestPhone: "",
  specialRequests: "",
  // Address fields
  guestAddress: "",
  guestCity: "",
  guestZipCode: "",
  guestCountry: "CH",
};

const defaultCustomerInfo: CustomerInfo = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  zipCode: "",
  country: "CH",
  specialRequests: "",
  paymentMethod: "credit-card",
};

// Create context
const BookingContext = createContext<BookingContextType | undefined>(undefined);

// Provider component
export const BookingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [selectedRooms, setSelectedRooms] = useState<RoomSelection[]>([]);
  const [selectedExtras, setSelectedExtras] = useState<ExtraService[]>([]);
  const [customerInfo, setCustomerInfo] =
    useState<CustomerInfo>(defaultCustomerInfo);
  const [totalPrice, setTotalPrice] = useState(0);
  const [confirmationNumber, setConfirmationNumber] = useState<string | null>(
    null
  );
  const [hotelSlug, setHotelSlug] = useState<string>("Hotel Adriatica");
  const [isEmbedded, setIsEmbedded] = useState<boolean>(false);
  const [selectedHotel, setSelectedHotel] = useState<SelectedHotel | null>(
    null
  );
  const [currentStep, setCurrentStep] = useState<number>(1);

  // New function to fetch and set hotel with fallback
  const fetchAndSetHotel = useCallback(async (slug: string) => {
    try {
      console.log("🔍 Fetching hotel for slug:", slug);

      // First, try to find by slug
      let { data: hotel, error } = await supabase
        .from("hotels")
        .select("id, name, slug")
        .eq("slug", slug)
        .maybeSingle(); // Use maybeSingle to handle 0 or multiple rows

      if (error) {
        console.warn("⚠️ Error finding hotel by slug:", error?.message);
      }

      if (!hotel) {
        console.warn(
          "⚠️ Hotel not found by slug, trying by name (case-insensitive)"
        );

        // Try by name (case-insensitive) - use maybeSingle to avoid multiple rows error
        ({ data: hotel, error } = await supabase
          .from("hotels")
          .select("id, name, slug")
          .ilike("name", `%${slug.replace("Hotel", "Hôtel")}%`)
          .limit(1)
          .maybeSingle());

        if (error) {
          console.warn(
            "⚠️ Error finding hotel by name with Hôtel:",
            error?.message
          );
        }

        // If still not found, try with original search term
        if (!hotel) {
          ({ data: hotel, error } = await supabase
            .from("hotels")
            .select("id, name, slug")
            .ilike("name", `%${slug}%`)
            .limit(1)
            .maybeSingle());

          if (error) {
            console.warn("⚠️ Error finding hotel by name:", error?.message);
          }
        }
      }

      if (!hotel) {
        console.warn(
          "⚠️ Hotel not found by name, falling back to first available hotel"
        );

        // Fallback: Get the first hotel (remove status filter if it doesn't exist)
        ({ data: hotel, error } = await supabase
          .from("hotels")
          .select("id, name, slug")
          .order("created_at", { ascending: true })
          .limit(1)
          .maybeSingle());

        if (error) {
          console.error("❌ Error finding fallback hotel:", error?.message);
        }
      }

      if (!hotel) {
        throw new Error("No hotels found in database");
      }

      console.log("✅ Selected hotel:", hotel.name, "(ID:", hotel.id, ")");
      setSelectedHotel(hotel);
    } catch (err) {
      console.error("❌ Error fetching hotel:", err);
      setSelectedHotel(null);
    }
  }, []); // Empty dependencies since it only uses supabase and setSelectedHotel

  const updateFormData = (field: keyof FormData, value: any) => {
    console.log(`📝 Updating form data: ${field} =`, value);
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateSelectedRooms = (rooms: RoomSelection[]) => {
    console.log("📋 Updating selected rooms in context:", rooms);

    // Validate room IDs
    rooms.forEach((room, index) => {
      if (!room.id) {
        console.error(`❌ Room at index ${index} has no ID:`, room);
      } else {
        console.log(
          `✅ Room ${index}: ID="${room.id}", Type="${room.type}", Price=${room.price}`
        );
      }
    });

    setSelectedRooms(rooms);
    calculateTotalPrice();
  };

  const updateSelectedExtras = (extras: ExtraService[]) => {
    console.log("Updating selected extras:", extras);
    setSelectedExtras(extras);
    calculateTotalPrice();
  };

  const updateCustomerInfo = (field: keyof CustomerInfo, value: string) => {
    setCustomerInfo((prev) => ({ ...prev, [field]: value }));
  };

  const calculateTotalPrice = () => {
    // Calculate number of nights
    let nights = 0;
    if (formData.checkIn && formData.checkOut) {
      nights = Math.ceil(
        (formData.checkOut.getTime() - formData.checkIn.getTime()) /
          (1000 * 60 * 60 * 24)
      );
    }

    // Calculate price from room selections
    const roomsPrice = selectedRooms.reduce((total, room) => {
      console.log("Room price calculation:", {
        room,
        nights,
        price: room.price * room.count * nights,
      });
      return total + room.price * room.count * nights;
    }, 0);

    // Calculate price from extras
    const extrasPrice = selectedExtras
      .filter((extra) => extra.selected)
      .reduce((total, extra) => {
        const quantity = extra.quantity || 1; // Use quantity if available, otherwise 1
        const extraTotal = extra.price * quantity;
        console.log("Extra price calculation:", {
          extra,
          quantity,
          price: extraTotal,
        });
        return total + extraTotal;
      }, 0);

    // Calculate total
    const calculatedTotal = roomsPrice + extrasPrice;
    console.log("Total price calculation:", {
      roomsPrice,
      extrasPrice,
      total: calculatedTotal,
    });
    setTotalPrice(calculatedTotal);
    return calculatedTotal;
  };

  const resetBooking = () => {
    console.log("🔄 Resetting booking data");
    setFormData(defaultFormData);
    setSelectedRooms([]);
    setSelectedExtras([]);
    setCustomerInfo(defaultCustomerInfo);
    setTotalPrice(0);
    setConfirmationNumber(null);
    setCurrentStep(1);
  };

  return (
    <BookingContext.Provider
      value={{
        formData,
        updateFormData,
        selectedRooms,
        updateSelectedRooms,
        selectedExtras,
        updateSelectedExtras,
        customerInfo,
        updateCustomerInfo,
        totalPrice,
        calculateTotalPrice,
        confirmationNumber,
        setConfirmationNumber,
        resetBooking,
        hotelSlug,
        setHotelSlug,
        isEmbedded,
        setIsEmbedded,
        selectedHotel,
        setSelectedHotel,
        fetchAndSetHotel,
        currentStep,
        setCurrentStep,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};

// Custom hook to use the booking context
export const useBooking = () => {
  const context = useContext(BookingContext);
  if (context === undefined) {
    throw new Error("useBooking must be used within a BookingProvider");
  }
  return context;
};
